import { useState, FC, useEffect } from "react";
import moment from "moment";
import DateDisplayArrow from "./date_display_arrows";
import CalendarModal from "./calendar_modal";
import {
  getCurrentDateTime,
  getMonthFirstDate,
  getMonthLastDate,
  getNextDate,
  getNextMonthFirstDate,
  getNextMonthLastDate,
  getNextWeekFirstDate,
  getNextWeekLastDate,
  getNextYearFirstDate,
  getNextYearLastDate,
  getPreviousDate,
  getPreviousMonthFirstDate,
  getPreviousMonthLastDate,
  getPreviousWeekFirstDate,
  getPreviousWeekLastDate,
  getPreviousYearFirstDate,
  getPreviousYearLastDate,
  getWeekFirstDate,
  getWeekLastDate,
  getYearFirstDate,
  getYearLastDate,
} from "../../../helpers/datetime";
import { TCalendarDate, TCalendarType, TDateType } from "./calendar_types";

const ONOCalendarV2: FC<{
  mode: TCalendarType;
  fromDate?: string;
  toDate?: string;
  onChangeDate: (fromDate: string, toDate: string, mode: string) => void;
  hideModes?: boolean;
}> = ({ mode, fromDate, toDate, onChangeDate, hideModes = false }) => {
  let [calendarDisplay, setCalendarDisplay] = useState<TCalendarDate>({
    fromDate: fromDate ?? moment(new Date()).format("YYYY-MM-DD"),
    toDate: toDate ?? moment(new Date()).format("YYYY-MM-DD"),
    mode: mode,
  });

  const [showCalendar, setShowCalendar] = useState(false);

  let [selectedDate, setSelectedDate] = useState<TCalendarDate>({
    fromDate: calendarDisplay.fromDate,
    toDate: calendarDisplay.toDate,
    mode: mode,
  });

  const onBack = () => {
    let calendarDate: TCalendarDate = {
      fromDate: calendarDisplay.fromDate,
      toDate: calendarDisplay.toDate,
      mode: calendarDisplay.mode,
    };
    if (calendarDisplay.mode == "DAILY") {
      calendarDate = {
        fromDate: getPreviousDate(calendarDisplay.fromDate),
        toDate: getPreviousDate(calendarDisplay.toDate),
        mode: calendarDisplay.mode,
      };
    } else if (calendarDisplay.mode == "WEEKLY") {
      calendarDate = {
        fromDate: getPreviousWeekFirstDate(calendarDisplay.fromDate),
        toDate: getPreviousWeekLastDate(calendarDisplay.toDate),
        mode: calendarDisplay.mode,
      };
    } else if (calendarDisplay.mode == "MONTHLY") {
      calendarDate = {
        fromDate: getPreviousMonthFirstDate(calendarDisplay.fromDate),
        toDate: getPreviousMonthLastDate(calendarDisplay.toDate),
        mode: calendarDisplay.mode,
      };
    } else if (calendarDisplay.mode == "YEARLY") {
      calendarDate = {
        fromDate: getPreviousYearFirstDate(calendarDisplay.fromDate),
        toDate: getPreviousYearLastDate(calendarDisplay.toDate),
        mode: calendarDisplay.mode,
      };
    }
    setCalendarDisplay(calendarDate);
    setSelectedDate(calendarDate);
    onChangeDate(calendarDate.fromDate, calendarDate.toDate, calendarDate.mode);
  };

  const onForward = () => {
    let calendarDate: TCalendarDate = {
      fromDate: calendarDisplay.fromDate,
      toDate: calendarDisplay.toDate,
      mode: calendarDisplay.mode,
    };
    if (calendarDisplay.mode == "DAILY") {
      calendarDate = {
        fromDate: getNextDate(calendarDisplay.fromDate),
        toDate: getNextDate(calendarDisplay.toDate),
        mode: calendarDisplay.mode,
      };
    } else if (calendarDisplay.mode == "WEEKLY") {
      calendarDate = {
        fromDate: getNextWeekFirstDate(calendarDisplay.fromDate),
        toDate: getNextWeekLastDate(calendarDisplay.toDate),
        mode: calendarDisplay.mode,
      };
    } else if (calendarDisplay.mode == "MONTHLY") {
      calendarDate = {
        fromDate: getNextMonthFirstDate(calendarDisplay.fromDate),
        toDate: getNextMonthLastDate(calendarDisplay.toDate),
        mode: calendarDisplay.mode,
      };
    } else if (calendarDisplay.mode == "YEARLY") {
      calendarDate = {
        fromDate: getNextYearFirstDate(calendarDisplay.fromDate),
        toDate: getNextYearLastDate(calendarDisplay.toDate),
        mode: calendarDisplay.mode,
      };
    }
    setCalendarDisplay(calendarDate);
    setSelectedDate(calendarDate);
    onChangeDate(calendarDate.fromDate, calendarDate.toDate, calendarDate.mode);
  };

  const onSelectDate = (
    date: string,
    mode: TCalendarType,
    dateType?: TDateType,
    toDate?: string
  ) => {
    if (mode == "ALL" || mode == "CUSTOM") {
      if (dateType == "FROM") {
        setSelectedDate({
          fromDate: date,
          toDate: selectedDate.toDate,
          mode: mode,
        });
      } else if (dateType == "TO") {
        setSelectedDate({
          fromDate: selectedDate.fromDate,
          toDate: date,
          mode: mode,
        });
      }
    } else if (mode == "DAILY") {
      setSelectedDate({
        fromDate: date,
        toDate: date,
        mode: mode,
      });
    } else if (mode == "WEEKLY") {
      if (dateType == "FROM") {
        setSelectedDate({
          fromDate: date,
          toDate: selectedDate.toDate,
          mode: mode,
        });
      } else if (dateType == "TO") {
        setSelectedDate({
          fromDate: selectedDate.fromDate,
          toDate: date,
          mode: mode,
        });
      } else if (dateType == "MONTH" || dateType == "YEAR") {
        setSelectedDate({
          fromDate: date,
          toDate: toDate ?? "",
          mode: mode,
        });
      } else {
        setSelectedDate({
          fromDate: getWeekFirstDate(date),
          toDate: getWeekLastDate(date),
          mode: mode,
        });
      }
    } else if (mode == "MONTHLY") {
      setSelectedDate({
        fromDate: date,
        toDate: getMonthLastDate(date),
        mode: mode,
      });
    } else if (mode == "YEARLY") {
      setSelectedDate({
        fromDate: date,
        toDate: getYearLastDate(date),
        mode: mode,
      });
    }
  };

  const onChangeMode = (mode: string) => {
    if (mode == "ALL") {
      setSelectedDate({
        ...selectedDate,
        fromDate: "2022-01-01",
        toDate: getCurrentDateTime(true),
        mode: mode,
      });
    } else if (mode == "DAILY") {
      setSelectedDate({
        ...selectedDate,
        fromDate: getCurrentDateTime(true),
        toDate: getCurrentDateTime(true),
        mode: mode,
      });
    } else if (mode == "WEEKLY") {
      setSelectedDate({
        ...selectedDate,
        fromDate: getWeekFirstDate(getCurrentDateTime(true)),
        toDate: getWeekLastDate(getCurrentDateTime(true)),
        mode: mode,
      });
    } else if (mode == "MONTHLY") {
      setSelectedDate({
        ...selectedDate,
        fromDate: getMonthFirstDate(getCurrentDateTime(true)),
        toDate: getMonthLastDate(getCurrentDateTime(true)),
        mode: mode,
      });
    } else if (mode == "YEARLY") {
      setSelectedDate({
        ...selectedDate,
        fromDate: getYearFirstDate(getCurrentDateTime(true)),
        toDate: getYearLastDate(getCurrentDateTime(true)),
        mode: mode,
      });
    } else if (mode == "CUSTOM") {
      setSelectedDate({
        ...selectedDate,
        fromDate: getCurrentDateTime(true),
        toDate: getCurrentDateTime(true),
        mode: mode,
      });
    }
  };

  useEffect(() => {
    setCalendarDisplay({
      fromDate: fromDate ?? moment(new Date()).format("YYYY-MM-DD"),
      toDate: toDate ?? moment(new Date()).format("YYYY-MM-DD"),
      mode: mode,
    });
  }, [fromDate, toDate, mode]);

  return (
    <>
      <div className="relative">
        <DateDisplayArrow
          fromDate={calendarDisplay.fromDate}
          toDate={calendarDisplay.toDate}
          mode={calendarDisplay.mode}
          onForward={onForward}
          onBack={onBack}
          onClick={() => {
            setShowCalendar(!showCalendar);
            if (!showCalendar) {
              setSelectedDate({
                fromDate: fromDate ?? moment(new Date()).format("YYYY-MM-DD"),
                toDate: toDate ?? moment(new Date()).format("YYYY-MM-DD"),
                mode: mode,
              });
              // For ALL Mode, set default dates on tap
              if (mode == "ALL") {
                setSelectedDate({
                  ...selectedDate,
                  fromDate: "2022-01-01",
                  toDate: getCurrentDateTime(true),
                  mode: mode,
                });
              }
            }
          }}
        />
        {showCalendar && (
          <CalendarModal
            showCalendar={showCalendar}
            setShowCalendar={setShowCalendar}
            calendarMode={selectedDate.mode}
            setCalendarMode={onChangeMode}
            fromDate={selectedDate.fromDate}
            toDate={selectedDate.toDate}
            onSelectDate={onSelectDate}
            hideModes={hideModes}
            onApply={() => {
              setShowCalendar(false);
              setCalendarDisplay(selectedDate);
              onChangeDate(
                selectedDate.fromDate,
                selectedDate.toDate,
                selectedDate.mode
              );
            }}
          />
        )}
      </div>
    </>
  );
};
export default ONOCalendarV2;
